import { useEffect, useMemo, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, UserMinus, RefreshCcw, ChevronRight } from 'lucide-react';

/** Design-Ops / Owner panel that surfaces teammates who have been marked
 *  inactive for more than 90 days. Reassign their leads / milestones /
 *  reports-to and hard-delete in one flow. */
export function PurgeQueuePanel({ activeUsers = [] }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [openReassign, setOpenReassign] = useState(null); // user summary row

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/purge-queue');
      setRows(r.data.rows || []);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(parseErr(e));
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.post('/admin/purge-queue/scan');
      toast.success(`Scan complete · ${r.data.candidates} candidate(s), ${r.data.newly_notified} new alert(s).`);
      await load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setScanning(false); }
  };

  if (loading) return null;
  if (!rows) return null;
  // Hide panel entirely when queue is empty (keeps /team clean for everyday viewing).
  if (rows.length === 0) return null;

  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'hsl(var(--warning) / .35)' }}
         data-testid="purge-queue-panel">
      <div className="px-4 py-3 flex items-center gap-3 bg-[hsl(var(--warning)/.08)]"
           style={{ borderBottom: '1px solid hsl(var(--warning) / .25)' }}>
        <AlertTriangle size={16} className="text-[hsl(var(--warning))]" />
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold">Teammates ready to be purged</div>
          <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
            Inactive for over 90 days. Reassign their work, then remove them from the directory permanently.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={runScan} disabled={scanning}
                data-testid="purge-queue-scan-btn">
          <RefreshCcw size={12} className="mr-1.5" />{scanning ? 'Scanning…' : 'Re-scan'}
        </Button>
      </div>
      <div className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
        {rows.map(r => (
          <PurgeRow key={r.id} row={r} onOpen={() => setOpenReassign(r)} />
        ))}
      </div>

      {openReassign && (
        <ReassignAndPurgeDialog
          candidate={openReassign}
          activeUsers={activeUsers}
          onClose={() => setOpenReassign(null)}
          onDone={() => { setOpenReassign(null); load(); }}
        />
      )}
    </div>
  );
}

function PurgeRow({ row, onOpen }) {
  const deps = row.dependencies || {};
  const blockingBits = [
    deps.creative_lead && `${deps.creative_lead} lead${deps.creative_lead > 1 ? 's' : ''} (creative)`,
    deps.technical_lead && `${deps.technical_lead} lead${deps.technical_lead > 1 ? 's' : ''} (technical)`,
    deps.architecture_lead && `${deps.architecture_lead} lead${deps.architecture_lead > 1 ? 's' : ''} (architecture)`,
    deps.milestones_owned && `${deps.milestones_owned} milestone${deps.milestones_owned > 1 ? 's' : ''}`,
    deps.direct_reports && `${deps.direct_reports} report${deps.direct_reports > 1 ? 's' : ''}`,
  ].filter(Boolean).join(' · ');
  const membershipBits = [
    deps.member_of && `${deps.member_of} team${deps.member_of > 1 ? 's' : ''}`,
  ].filter(Boolean).join(' · ');

  return (
    <button
      className="w-full text-left px-4 py-3 hover:bg-[hsl(var(--surface-1))] flex items-center gap-3"
      onClick={onOpen}
      data-testid={`purge-row-${row.id}`}
    >
      <div className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold text-[13px] flex-none"
           style={{ background: 'hsl(var(--warning))' }}>
        {(row.name || row.email || '?').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-medium truncate">{row.name || row.email}</div>
        <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] truncate">
          {row.email} · {row.days_inactive}d inactive
          {blockingBits && <> · <span className="text-[hsl(var(--warning))]">blocks: {blockingBits}</span></>}
          {!blockingBits && membershipBits && <> · {membershipBits}</>}
          {!blockingBits && !membershipBits && <> · no dependencies</>}
        </div>
      </div>
      <ChevronRight size={14} className="text-[hsl(var(--foreground-muted))] flex-none" />
    </button>
  );
}

function ReassignAndPurgeDialog({ candidate, activeUsers, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reassign, setReassign] = useState({
    creative_lead: {}, technical_lead: {}, architecture_lead: {},
    milestone_owner: {}, reports_to: {},
  });

  useEffect(() => {
    if (!candidate) return;
    let alive = true;
    api.get(`/admin/purge-queue/${candidate.id}`).then(r => {
      if (alive) setDetail(r.data);
    }).catch(e => toast.error(parseErr(e)));
    return () => { alive = false; };
  }, [candidate]);

  const eligibleReplacements = useMemo(
    () => (activeUsers || []).filter(u => u.id !== candidate?.id && u.status === 'active'),
    [activeUsers, candidate],
  );

  const setReassignField = (kind, key, value) => {
    setReassign(prev => ({ ...prev, [kind]: { ...prev[kind], [key]: value || null } }));
  };

  const submit = async (mode) => {
    // mode: 'reassigned' or 'anyway'. When 'anyway', we skip the reassignment map.
    const requiredPhrase = candidate?.email || '';
    if (confirmText.trim().toLowerCase() !== requiredPhrase.toLowerCase()) {
      toast.error(`Type "${requiredPhrase}" to confirm.`); return;
    }
    setBusy(true);
    try {
      const body = { confirm: true };
      if (mode === 'reassigned') body.reassign = reassign;
      await api.post(`/admin/purge-queue/${candidate.id}/purge`, body);
      toast.success(`${candidate.name || candidate.email} removed from the studio.`);
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const deps = detail?.dependencies || {};
  const totals = detail?.totals || {};

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[640px]" data-testid="purge-reassign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserMinus size={16} />Purge {candidate?.name || candidate?.email}
          </DialogTitle>
          <DialogDescription>
            Reassign this teammate&apos;s remaining work below. Rows left blank will be cleared
            (a &ldquo;purge anyway&rdquo; — safe, but may leave orphaned projects or milestones).
            This action is permanent.
          </DialogDescription>
        </DialogHeader>

        {!detail ? (
          <div className="py-8 text-center text-[hsl(var(--foreground-muted))]">Loading dependencies…</div>
        ) : (
          <div className="space-y-4 max-h-[420px] overflow-auto pr-1">
            {totals.blocking === 0 && (
              <div className="text-[12.5px] px-3 py-2 rounded-md bg-[hsl(var(--success)/.10)] text-[hsl(var(--success))]">
                No dependencies remain — this teammate can be purged directly.
              </div>
            )}

            <LeadBlock title="Creative Lead of" projects={deps.creative_lead_of}
                       kind="creative_lead" reassign={reassign.creative_lead}
                       users={eligibleReplacements} onSet={setReassignField} />
            <LeadBlock title="Technical Lead of" projects={deps.technical_lead_of}
                       kind="technical_lead" reassign={reassign.technical_lead}
                       users={eligibleReplacements} onSet={setReassignField} />
            <LeadBlock title="Architecture Lead of" projects={deps.architecture_lead_of}
                       kind="architecture_lead" reassign={reassign.architecture_lead}
                       users={eligibleReplacements} onSet={setReassignField} />

            {deps.milestones_owned?.length > 0 && (
              <div>
                <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))] mb-1.5">
                  Milestone owner ({deps.milestones_owned.length})
                </div>
                <div className="space-y-1.5">
                  {deps.milestones_owned.map(m => {
                    const key = `${m.project_id}::${m.milestone_index}`;
                    return (
                      <div key={key} className="grid grid-cols-[1fr_180px] items-center gap-2 border rounded-md px-3 py-2"
                           style={{ borderColor: 'hsl(var(--border))' }}>
                        <div className="min-w-0">
                          <div className="text-[12.5px] truncate">{m.milestone_name || `Milestone #${m.milestone_index + 1}`}</div>
                          <div className="text-[11px] text-[hsl(var(--foreground-muted))] truncate">
                            {m.project_name}{m.target_date ? ` · ${m.target_date}` : ''}
                          </div>
                        </div>
                        <Select value={reassign.milestone_owner[key] || ''}
                                onValueChange={(v) => setReassignField('milestone_owner', key, v)}>
                          <SelectTrigger data-testid={`purge-milestone-${key}`}>
                            <SelectValue placeholder="Reassign…" />
                          </SelectTrigger>
                          <SelectContent>
                            {eligibleReplacements.map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {deps.direct_reports?.length > 0 && (
              <div>
                <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))] mb-1.5">
                  Direct reports ({deps.direct_reports.length})
                </div>
                <div className="space-y-1.5">
                  {deps.direct_reports.map(sub => (
                    <div key={sub.id} className="grid grid-cols-[1fr_180px] items-center gap-2 border rounded-md px-3 py-2"
                         style={{ borderColor: 'hsl(var(--border))' }}>
                      <div className="min-w-0">
                        <div className="text-[12.5px] truncate">{sub.name || sub.email}</div>
                        <div className="text-[11px] text-[hsl(var(--foreground-muted))]">{sub.role}</div>
                      </div>
                      <Select value={reassign.reports_to[sub.id] || ''}
                              onValueChange={(v) => setReassignField('reports_to', sub.id, v)}>
                        <SelectTrigger data-testid={`purge-reports-to-${sub.id}`}>
                          <SelectValue placeholder="New manager…" />
                        </SelectTrigger>
                        <SelectContent>
                          {eligibleReplacements.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {deps.member_of?.length > 0 && (
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] border rounded-md px-3 py-2"
                   style={{ borderColor: 'hsl(var(--border))' }}>
                Also a team member on {deps.member_of.length} project
                {deps.member_of.length === 1 ? '' : 's'} — they&apos;ll be removed automatically.
              </div>
            )}

            {deps.open_commitments > 0 && (
              <div className="text-[12px] text-[hsl(var(--warning))] border rounded-md px-3 py-2"
                   style={{ borderColor: 'hsl(var(--warning)/.35)' }}>
                They have {deps.open_commitments} open commitment{deps.open_commitments === 1 ? '' : 's'} —
                these are historical records and stay in place after purge.
              </div>
            )}

            <div>
              <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))] mb-1">
                Confirm
              </div>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={`Type "${candidate?.email}" to confirm`}
                data-testid="purge-confirm-input"
                className="w-full border rounded-md px-3 py-2 text-[12.5px] bg-[hsl(var(--card))]"
                style={{ borderColor: 'hsl(var(--border))' }}
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="outline" onClick={() => submit('anyway')} disabled={busy || !detail}
                  data-testid="purge-anyway-btn">
            Purge anyway
          </Button>
          <Button variant="destructive" onClick={() => submit('reassigned')} disabled={busy || !detail}
                  data-testid="purge-reassign-btn">
            {busy ? 'Purging…' : 'Reassign & purge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadBlock({ title, projects, kind, reassign, users, onSet }) {
  if (!projects?.length) return null;
  return (
    <div>
      <div className="text-[12px] font-semibold uppercase tracking-wider text-[hsl(var(--foreground-muted))] mb-1.5">
        {title} ({projects.length})
      </div>
      <div className="space-y-1.5">
        {projects.map(p => (
          <div key={p.id} className="grid grid-cols-[1fr_180px] items-center gap-2 border rounded-md px-3 py-2"
               style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="min-w-0">
              <div className="text-[12.5px] truncate">{p.project_name || 'Untitled project'}</div>
              <div className="text-[11px] text-[hsl(var(--foreground-muted))] truncate">
                {p.client || '—'}
              </div>
            </div>
            <Select value={reassign[p.id] || ''} onValueChange={(v) => onSet(kind, p.id, v)}>
              <SelectTrigger data-testid={`purge-${kind}-${p.id}`}>
                <SelectValue placeholder="Reassign lead…" />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}
